import { ClassNameMap } from '@mui/material';
import { ClassKeyOfStyles } from '@mui/styles';

// Types
import { TranslateFunction } from '../../../core/types/TypographyTypes';
// Constants
import { COUNTRY_WISE_BANK_DETAILS_FIELDS } from '../../../core/utils/Constants/Constants';

interface BecomePartnerFieldMappingParams {
    translate: TranslateFunction;
    classes: ClassNameMap<ClassKeyOfStyles<any>>;
    isManualEdit: boolean;
    countryInput: string;
}

export const isCountryPresent = (countryCode: string) => {
    return COUNTRY_WISE_BANK_DETAILS_FIELDS.some(field => field.values.includes(countryCode));
};

export const formatSortCode = (value: string): string => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 6);
    if (limited.length <= 2) return limited;
    if (limited.length <= 4) return `${limited.slice(0, 2)}-${limited.slice(2)}`;
    return `${limited.slice(0, 2)}-${limited.slice(2, 4)}-${limited.slice(4, 6)}`;
};

export const getSortCodeForPayload = (value: string): string => (value || '').replace(/\D/g, '');

export const getBankDetailsFieldMappingDefinition = ({
    translate,
    classes,
    isManualEdit,
    countryInput
}: BecomePartnerFieldMappingParams) => {
    const addBankDetailsFieldMappingDefinition = [
        {
            name: 'bankDetailsHeading',
            component: 'sectionHeading',
            label: 'add.bank.details.form.account.heading',
            additionalProps: {
                className: classes.formSectionHeading,
                variant: 'body1'
            }
        },
        {
            name: 'accountName',
            component: 'text',
            label: 'add.bank.details.form.accountname.label',
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'currency',
            component: 'select',
            label: 'add.bank.details.form.currency.label',
            additionalProps: { className: classes.formSectionInput, type: 'currency' }
        },
        {
            name: 'country',
            component: 'select',
            label: 'add.bank.details.form.country.label',
            additionalProps: { className: classes.formSectionInput, type: 'country' }
        },
        {
            name: 'bankDetailsHeading',
            component: 'sectionHeading',
            label: 'add.bank.details.form.bank.heading',
            conditionalRender: (watch) => watch(['accountName', 'currency', 'country']).every(
                (field: string | null | undefined) => field !== "" && field !== null && field !== undefined
            ),
            additionalProps: {
                className: classes.formSectionHeading,
                variant: 'body1'
            }
        },
        {
            name: 'accountNumber',
            component: 'text',
            label: 'add.bank.details.form.account.number.label',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if ((currency === 'GBP' && country === 'GBR')) {
                    return true;
                }

                const shouldRenderDefault: boolean = isCountryPresent(country);

                if (!shouldRenderDefault) {
                    return true;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'accountNumber');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'sortCode',
            component: 'text',
            label: 'add.bank.details.form.sort.code.label',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return true;
                }

                return false;
            },
            additionalProps: {
                className: classes.formSectionInput,
                isCoralogixMasked: true,
                handleChange: (event: any) => {
                    event.target.value = formatSortCode(event.target.value);
                },
            }
        },
        {
            name: 'bsbNumber',
            component: 'text',
            label: 'add.bank.details.form.bsb_number',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'bsbNumber');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'transitNumber',
            component: 'text',
            label: 'add.bank.details.form.transit_number',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'transitNumber');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'abaNumber',
            component: 'text',
            label: countryInput === 'USA' ? 'add.bank.details.form.aba_number.usa' : 'add.bank.details.form.aba_number',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'abaNumber');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'branchCode',
            component: 'text',
            label: 'add.bank.details.form.branch_code',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'branchCode');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'ifscCode',
            component: 'text',
            label: 'add.bank.details.form.ifsc_code',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'ifscCode');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'iban',
            component: 'text',
            label: 'add.bank.details.form.iban',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'iban');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'cnapsCode',
            component: 'text',
            label: 'add.bank.details.form.cnaps_code',
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'cnapsCode');

                return accountNumberField?.values.includes(country) ?? false;
            },
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true }
        },
        {
            name: 'swiftCode',
            component: 'text',
            label: 'add.bank.details.form.summary.bank.swift.label',
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true },
            conditionalRender: (watch) => {
                const [accountName, currency, country] = watch(['accountName', 'currency', 'country']);

                if (!accountName || !currency || !country) {
                    return false;
                }

                if (currency === 'GBP' && country === 'GBR') {
                    return false;
                }
                const shouldRenderDefault: boolean = isCountryPresent(country);

                if (!shouldRenderDefault) {
                    return true;
                }

                const accountNumberField = COUNTRY_WISE_BANK_DETAILS_FIELDS.find(({ type }) => type === 'swiftCode');

                return accountNumberField?.values.includes(country) ?? false;
            },
        },
        {
            name: 'affiliateBank',
            component: 'text',
            label: 'add.bank.details.form.summary.bank.name.label',
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true },
            conditionalRender: (watch) => watch(['accountName', 'currency', 'country']).every(
                (field: string | null | undefined) => field !== "" && field !== null && field !== undefined && isManualEdit
            ),
        },
        {
            name: 'branchAddress',
            component: 'text',
            label: 'add.bank.details.form.summary.bank.address.label',
            additionalProps: { className: classes.formSectionInput, isCoralogixMasked: true },
            conditionalRender: (watch) => watch(['accountName', 'currency', 'country']).every(
                (field: string | null | undefined) => field !== "" && field !== null && field !== undefined && isManualEdit
            ),
        }
    ];

    return addBankDetailsFieldMappingDefinition;
}
